import React, { useState, useCallback, useEffect } from 'react';

// Cloud Functions für das externe WorldDB / SalesGod Account-Setup.
const PREPARE_FN_URL =
  process.env.REACT_APP_WORLDDB_PREPARE_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/prepareWorldDbSetup';
const SEND_FN_URL =
  process.env.REACT_APP_WORLDDB_SEND_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/sendWorldDbSetup';
const STATUS_FN_URL =
  process.env.REACT_APP_WORLDDB_STATUS_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/worldDbSetupStatus';

const KEY_STORE = 'rp-worlddb-key';

// Säubert den Account-Key von Copy-Paste-Müll: umschließende Anführungszeichen
// und ALLE Whitespace-Zeichen (inkl. NBSP, Zero-Width-Spaces, BOM, Zeilenumbrüche).
const sanitizeKey = (raw) => String(raw == null ? '' : raw)
  .replace(/[\s\u00A0\u200B-\u200D\uFEFF]+/g, '')
  .replace(/^["']+|["']+$/g, '')
  .trim();

// Holt die aussagekräftigste Fehlermeldung aus der Antwort der Cloud Function.
// Bevorzugt die echte Meldung des Zielsystems (data.response.error).
const extractError = (data, fallback) => {
  if (data && data.response && (data.response.error || data.response.message)) {
    const base = data.error ? `${data.error}` : '';
    const detail = data.response.error || data.response.message;
    return base && !base.includes(detail) ? `${base} – ${detail}` : detail;
  }
  return (data && data.error) || fallback;
};

const THRESHOLDS = [
  { v: 0, label: 'Alle' },
  { v: 6, label: '≥ 6' },
  { v: 7, label: '≥ 7' },
  { v: 8, label: '≥ 8' },
];

const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const Field = ({ label, value }) => {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return null;
  let shown;
  if (Array.isArray(value)) shown = value.join(' · ');
  else if (typeof value === 'object') shown = JSON.stringify(value);
  else shown = String(value);
  return <p className="sg-kv"><span>{label}</span> {shown}</p>;
};

const PayloadPreview = ({ payload }) => {
  if (!payload) return null;
  const { identity = {}, audience = {}, brain = {}, campaign = {} } = payload;
  return (
    <div className="sg-preview">
      <div className="sg-preview-block">
        <h4>identity · wer der Account ist</h4>
        <Field label="aboutUs" value={identity.aboutUs} />
        <Field label="company" value={identity.company} />
        <Field label="website" value={identity.website} />
        <Field label="offer" value={identity.offer} />
        <Field label="usps" value={identity.usps} />
        <Field label="sender" value={[identity.senderName, identity.senderRole, identity.senderEmail].filter(Boolean).join(', ')} />
        <Field label="emojis" value={identity.emojis ? 'ja' : 'nein'} />
        <Field label="language" value={identity.language} />
      </div>
      <div className="sg-preview-block">
        <h4>audience · Zielgruppe</h4>
        <Field label="description" value={audience.description} />
        <Field label="painPoints" value={audience.painPoints} />
        <Field label="industry" value={audience.industry} />
        <Field label="region" value={audience.region} />
        <Field label="decisionMakerRole" value={audience.decisionMakerRole} />
        <Field label="companySize" value={audience.companySize} />
        <Field label="excludeIndustries" value={audience.excludeIndustries} />
      </div>
      <div className="sg-preview-block">
        <h4>brain · Copywriting-Voice</h4>
        <Field label="emails (Bodies)" value={`${(brain.emails || []).length} Stück`} />
        <Field label="subjectLines" value={brain.subjectLines} />
        <Field label="tone" value={brain.tone} />
        <Field label="voiceDescription" value={brain.voiceDescription} />
        <Field label="taboos" value={brain.taboos} />
        <Field label="signOff" value={brain.signOff} />
      </div>
      <div className="sg-preview-block">
        <h4>campaign · Template-Brief</h4>
        <Field label="uniqueAdvantage" value={campaign.uniqueAdvantage} />
        <Field label="painPoint" value={campaign.painPoint} />
        <Field label="goal" value={campaign.goal} />
        <Field label="angles" value={campaign.angles} />
        <Field label="references" value={campaign.references} />
        <Field label="fixedFacts" value={campaign.fixedFacts} />
        <Field label="proof" value={campaign.proof} />
        <Field label="extraRules" value={campaign.extraRules} />
        <Field label="language" value={campaign.language} />
      </div>
    </div>
  );
};

// Ein-/ausklappbarer Bereich. Alles, was nicht zum reinen "Connect"-Flow gehört,
// steckt darin und ist standardmäßig zugeklappt – so bleibt das Modal simpel.
const Collapsible = ({ title, hint, badge, tone, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`sg-collapse ${open ? 'is-open' : ''} ${tone ? `sg-collapse-${tone}` : ''}`}>
      <button type="button" className="sg-collapse-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="sg-collapse-title">{title}</span>
        {badge != null && <span className="sg-collapse-badge">{badge}</span>}
        {hint && !open && <span className="sg-collapse-hint">{hint}</span>}
        <svg className="sg-collapse-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && <div className="sg-collapse-body">{children}</div>}
    </div>
  );
};

// Vollständiges Launch-Modal: Auswahl -> Vorschau -> Senden -> Bestätigung.
// Entwürfe (Payload + Optionen + Zeitstempel) werden über onSaveDraft im Report
// gespeichert, damit ein fehlgeschlagener Versand erneut geschickt werden kann.
const SalesGodLaunch = ({ open, onClose, slug, draft, onSaveDraft, getToken }) => {
  const d = draft || {};
  const dOpts = d.options || {};
  const [bodyMin, setBodyMin] = useState(dOpts.bodyMin != null ? dOpts.bodyMin : 7);
  const [subjectMin, setSubjectMin] = useState(dOpts.subjectMin != null ? dOpts.subjectMin : 7);

  const [apiKey, setApiKey] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [payload, setPayload] = useState(d.payload || null);
  const [warnings, setWarnings] = useState([]);
  const [stats, setStats] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | preparing | sending | sent | checking
  const [error, setError] = useState('');
  const [sentInfo, setSentInfo] = useState(d.lastRunId ? { runId: d.lastRunId, at: d.lastSentAt } : null);
  const [statusResult, setStatusResult] = useState(null);

  // Eingegebenen Account-Key pro Browser merken (NICHT im Report speichern).
  useEffect(() => {
    if (!open) return;
    try { const k = localStorage.getItem(KEY_STORE); if (k) setApiKey(k); } catch (e) { /* ignore */ }
  }, [open]);

  const authHeaders = useCallback(async () => {
    const token = getToken ? await getToken() : null;
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  }, [getToken]);

  // Feste, sinnvolle Defaults: Diese SalesGod-internen Flags werden bewusst nicht
  // mehr im UI angeboten – wir schicken einfach die Daten mit den passenden Werten.
  const options = {
    bodyMin,
    subjectMin,
    skipLeadResearch: true,
    activateCloneTemplate: true,
    skipTemplates: false,
    forceFullSetup: false,
  };

  const prepare = useCallback(async () => {
    if (!slug || phase === 'preparing') return;
    setPhase('preparing'); setError(''); setStatusResult(null);
    try {
      const resp = await fetch(PREPARE_FN_URL, {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({ slug, options }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.ok) {
        console.error('[SalesGod] Inhalte generieren fehlgeschlagen', {
          httpStatus: resp.status,
          slug,
          error: data.error,
          response: data.response,
          fullData: data,
        });
        throw new Error(extractError(data, `Vorschau fehlgeschlagen (HTTP ${resp.status}).`));
      }
      setPayload(data.payload);
      setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setStats(data.stats || null);
      setPhase('idle');
    } catch (e) {
      console.error('[SalesGod] Fehler beim Generieren der Inhalte:', e.message, e);
      setError(e.message || 'Vorschau fehlgeschlagen.');
      setPhase('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, phase, authHeaders, bodyMin, subjectMin]);

  const send = useCallback(async () => {
    if (!payload || phase === 'sending') return;
    const key = sanitizeKey(apiKey);
    if (!key) { setError('Bitte zuerst den Account-Key (Secret) eingeben.'); return; }
    try { localStorage.setItem(KEY_STORE, key); } catch (e) { /* ignore */ }
    setPhase('sending'); setError('');
    try {
      const resp = await fetch(SEND_FN_URL, {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({ slug, payload, apiKey: key, options }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.ok) {
        // Fehlerantwort vom Zielsystem vollständig in die Konsole loggen,
        // damit man die genaue Fehlermeldung von SalesGod nachvollziehen kann.
        console.error('[SalesGod] Senden fehlgeschlagen', {
          httpStatus: resp.status,
          slug,
          error: data.error,
          targetStatus: data.status,
          response: data.response,
          fullData: data,
        });
        throw new Error(extractError(data, `Senden fehlgeschlagen (HTTP ${resp.status}).`));
      }
      const at = Date.now();
      setSentInfo({ runId: data.runId || '', at });
      setPhase('sent');
      // Erfolgreich gesendet: als gesendet vermerken (Entwurf bleibt als Referenz erhalten).
      if (onSaveDraft) onSaveDraft({ payload, options, lastSentAt: at, lastRunId: data.runId || '', lastStatus: 'sent', lastError: '' });
    } catch (e) {
      console.error('[SalesGod] Fehler beim Senden:', e.message, e);
      setError(e.message || 'Senden fehlgeschlagen.');
      setPhase('idle');
      // Fehlgeschlagen: Payload als Entwurf sichern, damit man ihn erneut schicken kann.
      if (onSaveDraft) onSaveDraft({ payload, options, draftSavedAt: Date.now(), lastStatus: 'draft', lastError: e.message || 'Senden fehlgeschlagen.' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, phase, apiKey, authHeaders, slug, onSaveDraft, bodyMin, subjectMin]);

  const checkStatus = useCallback(async () => {
    const runId = sentInfo && sentInfo.runId;
    if (!runId || phase === 'checking') return;
    setPhase('checking'); setError('');
    try {
      const resp = await fetch(STATUS_FN_URL, {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({ runId, apiKey: sanitizeKey(apiKey) }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.ok === false) {
        console.error('[SalesGod] Status-Abruf fehlgeschlagen', {
          httpStatus: resp.status,
          runId,
          error: data.error,
          response: data.response,
          fullData: data,
        });
      }
      setStatusResult(data.response || data);
    } catch (e) {
      console.error('[SalesGod] Fehler beim Status-Abruf:', e.message, e);
      setError(e.message || 'Status-Abruf fehlgeschlagen.');
    }
    setPhase('idle');
  }, [sentInfo, phase, authHeaders, apiKey]);

  if (!open) return null;

  const seg = (val, set, items) => (
    <div className="sg-seg">
      {items.map((it) => (
        <button key={it.v} type="button" className={`sg-seg-btn ${val === it.v ? 'is-on' : ''}`} onClick={() => set(it.v)}>{it.label}</button>
      ))}
    </div>
  );

  // Aktueller Schritt im Flow: Konfigurieren → Payload → Senden.
  const sent = phase === 'sent' || !!(sentInfo && sentInfo.runId);
  const step = sent ? 3 : payload ? 2 : 1;
  const steps = [
    { n: 1, label: 'Konfigurieren' },
    { n: 2, label: 'Inhalte generieren' },
    { n: 3, label: 'Senden' },
  ];

  return (
    <div className="sg-overlay" onClick={onClose}>
      <div className="sg-modal" onClick={(e) => e.stopPropagation()}>
        <header className="sg-head">
          <div className="sg-head-main">
            <span className="sg-badge" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
            </span>
            <div>
              <h2 className="sg-title">Mit SalesGod verbinden</h2>
              <p className="sg-sub">Key eingeben, Inhalte generieren, an den Account senden.</p>
            </div>
          </div>
          <button type="button" className="sg-close" onClick={onClose} aria-label="Schließen">×</button>

          <ol className="sg-steps" aria-label="Fortschritt">
            {steps.map((s, i) => (
              <li
                key={s.n}
                className={`sg-step ${step === s.n ? 'is-active' : ''} ${step > s.n ? 'is-done' : ''}`}
              >
                <span className="sg-step-dot">{step > s.n ? '✓' : s.n}</span>
                <span className="sg-step-label">{s.label}</span>
                {i < steps.length - 1 && <span className="sg-step-line" aria-hidden="true" />}
              </li>
            ))}
          </ol>
        </header>

        <div className="sg-body">
          {/* Aktuelle Rückmeldung – einmal, kompakt. Details landen in der Konsole. */}
          {error && <div className="sg-msg sg-msg-err">{error}</div>}
          {phase === 'sent' && <div className="sg-msg sg-msg-ok">Erfolgreich gesendet{sentInfo && sentInfo.runId ? ` · Run-ID ${sentInfo.runId}` : ''}. Bestätigt vom Zielsystem.</div>}

          {/* ── Account-Key: das eigentliche „Verbinden" ── */}
          <section className="sg-section">
            <h3 className="sg-section-title">
              <span className="sg-section-ico" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="15" r="4" /><path d="m10.85 12.15 8.15-8.15" /><path d="m18 5 2 2" /><path d="m15 8 2 2" /></svg>
              </span>
              Account-Key (Secret)
            </h3>
            <div className="sg-key-wrap">
              <svg className="sg-key-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="8" cy="15" r="4" /><path d="m10.85 12.15 8.15-8.15" /><path d="m18 5 2 2" /><path d="m15 8 2 2" /></svg>
              <input
                className="sg-key"
                type="password"
                value={apiKey}
                placeholder="x-api-key des Ziel-Accounts einfügen"
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="off"
              />
            </div>
            <p className="sg-hint">Bestimmt, an welchen Account gesendet wird. Wird nur in diesem Browser gemerkt, nicht im Report gespeichert.</p>
          </section>

          {/* ── Aktionen: der Kern-Flow ── */}
          <div className="sg-actions">
            <button type="button" className={`sg-btn sg-btn-primary ${phase === 'preparing' ? 'is-loading' : ''}`} onClick={prepare} disabled={phase === 'preparing'}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v3" /><path d="M12 18v3" /><path d="m5.6 5.6 2.1 2.1" /><path d="m16.3 16.3 2.1 2.1" /><path d="M3 12h3" /><path d="M18 12h3" /><path d="m5.6 18.4 2.1-2.1" /><path d="m16.3 7.7 2.1-2.1" /></svg>
              {phase === 'preparing' ? 'Generiere Inhalte …' : (payload ? 'Inhalte neu generieren' : 'Inhalte generieren')}
            </button>
            {payload && (
              <button type="button" className="sg-btn sg-btn-send" onClick={send} disabled={phase === 'sending'}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
                {phase === 'sending' ? 'Sende …' : 'An SalesGod senden'}
              </button>
            )}
          </div>

          {warnings.map((w, i) => <div key={i} className="sg-msg sg-msg-warn">{w}</div>)}
          {stats && (
            <p className="sg-hint">Ausgewählt: {stats.bodiesSelected} Text(e), {stats.subjectLinesSelected} Betreffzeile(n).{stats.fallbackUsed ? ' Auffüllung aktiv.' : ''}</p>
          )}

          {/* ── Vorschau (zugeklappt) ── */}
          {payload && (
            <Collapsible title="Vorschau der Inhalte" hint="generierte E-Mails & Brief">
              <div className="sg-collapse-tools">
                <button type="button" className="sg-btn sg-btn-ghost sg-btn-sm" onClick={() => setShowRaw((v) => !v)}>
                  {showRaw ? 'Lesbar' : 'Roh-JSON'}
                </button>
              </div>
              {showRaw
                ? <pre className="sg-raw">{JSON.stringify(payload, null, 2)}</pre>
                : <PayloadPreview payload={payload} />}
            </Collapsible>
          )}

          {/* ── Erweiterte Einstellungen (zugeklappt) ── */}
          <Collapsible title="Erweiterte Einstellungen" hint="E-Mail-Auswahl fürs Brain">
            <div className="sg-row"><span className="sg-row-label">E-Mails ab Bewertung</span>{seg(bodyMin, setBodyMin, THRESHOLDS)}</div>
            <div className="sg-row"><span className="sg-row-label">Betreffzeilen ab Bewertung</span>{seg(subjectMin, setSubjectMin, THRESHOLDS)}</div>
            <p className="sg-hint">„Alle" nimmt auch unbewertete E-Mails. Reicht es nicht für das Minimum (≥2), wird mit den bestbewerteten aufgefüllt.</p>
          </Collapsible>

          {/* ── Status (zugeklappt) ── */}
          {sentInfo && sentInfo.runId && (
            <Collapsible title="Status" hint="beim Zielsystem nachfragen">
              <button type="button" className="sg-btn sg-btn-ghost sg-btn-sm" onClick={checkStatus} disabled={phase === 'checking'}>
                {phase === 'checking' ? 'Prüfe …' : 'Status prüfen'}
              </button>
              {statusResult && <pre className="sg-raw" style={{ marginTop: 10 }}>{JSON.stringify(statusResult, null, 2)}</pre>}
            </Collapsible>
          )}

          {/* ── Letzter Stand (zugeklappt) ── */}
          {(d.lastSentAt || d.draftSavedAt) && (
            <Collapsible
              title="Letzter Stand"
              tone={d.lastStatus === 'sent' ? 'ok' : 'warn'}
              hint={d.lastStatus === 'sent' ? `gesendet ${fmtTs(d.lastSentAt)}` : `Entwurf ${fmtTs(d.draftSavedAt)}`}
            >
              <p className="sg-kv">
                {d.lastStatus === 'sent'
                  ? `Zuletzt gesendet am ${fmtTs(d.lastSentAt)}${d.lastRunId ? ` · Run-ID ${d.lastRunId}` : ''}.`
                  : `Entwurf gespeichert am ${fmtTs(d.draftSavedAt)}${d.lastError ? ` · letzter Fehler: ${d.lastError}` : ''}.`}
              </p>
            </Collapsible>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesGodLaunch;
